/**
 * PKCE utilities for Criipto BankID authentication (web-only)
 * Adapted from frontend/lib/utils/bankidpkceflow.ts without React Native deps
 */

const CRIIPTO_CONFIG = {
  clientId: "urn:my:application:identifier:850359",
  domain: "https://amino.criipto.id",
  acrValues: "urn:grn:authn:no:bankid:substantial",
} as const;

const STORAGE_KEYS = {
  PKCE_VERIFIER: "pkce_verifier",
  PKCE_REDIRECT_URI: "pkce_redirect_uri",
} as const;

// ── PKCE challenge generation ──────────────────────────────

function generateRandomString(length: number): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, "0")).join("").slice(0, length);
}

async function sha256(plain: string): Promise<ArrayBuffer> {
  const encoder = new TextEncoder();
  return crypto.subtle.digest("SHA-256", encoder.encode(plain));
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export async function generatePKCEChallenge(): Promise<{ codeVerifier: string; codeChallenge: string }> {
  const codeVerifier = generateRandomString(128);
  const hash = await sha256(codeVerifier);
  const codeChallenge = base64UrlEncode(hash);
  return { codeVerifier, codeChallenge };
}

// ── Session storage helpers ────────────────────────────────

export function setPKCEVerifier(verifier: string, redirectUri: string): void {
  sessionStorage.setItem(STORAGE_KEYS.PKCE_VERIFIER, verifier);
  sessionStorage.setItem(STORAGE_KEYS.PKCE_REDIRECT_URI, redirectUri);
}

export function getPKCEVerifier(): { verifier: string; redirectUri: string } | null {
  const verifier = sessionStorage.getItem(STORAGE_KEYS.PKCE_VERIFIER);
  const redirectUri = sessionStorage.getItem(STORAGE_KEYS.PKCE_REDIRECT_URI);
  if (!verifier || !redirectUri) return null;
  return { verifier, redirectUri };
}

export function clearPKCEVerifier(): void {
  sessionStorage.removeItem(STORAGE_KEYS.PKCE_VERIFIER);
  sessionStorage.removeItem(STORAGE_KEYS.PKCE_REDIRECT_URI);
}

// ── URL builders ───────────────────────────────────────────

export function getRedirectUri(): string {
  return window.location.origin + "/auth/callback";
}

export function buildCriiptoAuthUrl(codeChallenge: string, redirectUri: string): string {
  const authUrl = new URL(`${CRIIPTO_CONFIG.domain}/oauth2/authorize`);
  authUrl.searchParams.set("client_id", CRIIPTO_CONFIG.clientId);
  authUrl.searchParams.set("redirect_uri", redirectUri);
  authUrl.searchParams.set("response_type", "code");
  authUrl.searchParams.set("scope", "openid");
  authUrl.searchParams.set("acr_values", CRIIPTO_CONFIG.acrValues);
  authUrl.searchParams.set("code_challenge", codeChallenge);
  authUrl.searchParams.set("code_challenge_method", "S256");
  return authUrl.toString();
}

// ── Token exchange ─────────────────────────────────────────

export async function exchangeCodeForIDToken(
  code: string,
  codeVerifier: string,
  redirectUri: string,
): Promise<string> {
  const tokenUrl = `${CRIIPTO_CONFIG.domain}/oauth2/token`;

  const res = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      code,
      redirect_uri: redirectUri,
      client_id: CRIIPTO_CONFIG.clientId,
      code_verifier: codeVerifier,
    }).toString(),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(`Token exchange failed: ${err.error_description || res.statusText}`);
  }

  const data = await res.json();
  if (!data.id_token) throw new Error("No ID token received from Criipto");
  return data.id_token;
}
